import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Printer } from "lucide-react";
import ReceiptTemplate from "./ReceiptTemplate";
import { useState, useEffect } from "react";
import api from "@/services/api";

// Define the type for the collection record API response
interface CollectionRecordApiResponse {
  id: string;
  organizationId: string;
  consumerId: string;
  collectionId: string;
  billingDate: string;
  dueDate: string;
  amount: number;
  totalPaid: number;
  status: string;
  entryNumber: string;
  notes: string;
  createdAt: string;
  updatedAt: string;
  consumer: {
    id: string;
    fullName: string;
    registrationNumber: string;
    contactNumber: string;
  };
  collection: {
    id: string;
    name: string;
    entryNumber: string;
  };
  payments: Array<{
    id: string;
    amount: number;
    paymentDate: string;
    transactionNumber: string;
    paymentMethod: string;
    description: string;
    collectedBy: {
      id: string;
      name: string;
      mobile: string;
    };
    creditAccount: {
      id: string;
      name: string;
    };
    debitAccount: {
      id: string;
      name: string;
    };
  }>;
  organization: {
    id: string;
    name: string;
    logo: string;
  };
}

interface PrintReceiptDialogProps {
  recordId: string;
}

const PrintReceiptDialog = ({ recordId }: PrintReceiptDialogProps) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [collectionRecord, setCollectionRecord] =
    useState<CollectionRecordApiResponse | null>(null);

  const fetchCollectionRecord = async () => {
    setLoading(true);
    try {
      const res = await api.payment.getPaymentById(recordId);
      setCollectionRecord(res.data);
    } catch (e) {
      setCollectionRecord(null);
    } finally {
      setLoading(false);
    }
  };

  // Fetch collection record when dialog is opened
  useEffect(() => {
    if (open) {
      fetchCollectionRecord();
    }
  }, [open, recordId]);

  const handlePrint = () => {
    window.print();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon">
          <Printer className="h-4 w-4" />
          <span className="sr-only">Print Receipt</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl w-full h-[90vh] print:max-w-none print:p-0 print:shadow-none p-2 sm:p-6 flex flex-col items-center">
        <DialogHeader className="print:hidden w-full">
          <DialogTitle>Print Receipt</DialogTitle>
        </DialogHeader>
        <div className="print:block w-full flex-1 overflow-y-auto">
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : collectionRecord ? (
            <ReceiptTemplate collectionRecord={collectionRecord} />
          ) : (
            <div className="text-center py-8 text-red-500">
              Failed to load collection record details.
            </div>
          )}
        </div>
        <div className="w-full flex justify-end mt-4 print:hidden">
          <Button
            onClick={handlePrint}
            disabled={loading || !collectionRecord}
            className="w-auto"
          >
            <Printer className="h-4 w-4 mr-2" />
            Print Receipt
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PrintReceiptDialog;
